import Activity from "./Activity.jsx";
import Knowledge from "./Knowledge.jsx";
import Agents from "./Agents.jsx";
import Attended from "./Attended.jsx";
import Outbound from "./Outbound.jsx";
import Spending from "./Spending.jsx";
import ThisApp from "./ThisApp.jsx";
import Wiring from "./Wiring.jsx";

/**
 * Seven screens, and none of them is governed by tags.
 *
 * `gate` rather than an effective intersection, because `Permission`
 * rendering an empty intersection reads as *permitted nothing* — which would
 * be a false sentence on screens that are permitted plenty and simply not by
 * that mechanism.
 *
 * It said "three screens" until this commit, and had said so since there were
 * three. Nothing broke, which is the point: a count in a comment is a fact
 * with nothing checking it, and the same sentence is in `App.jsx` describing
 * a nav that had four more items than it claimed.
 */

/**
 * The same three headings the console uses, and deliberately so.
 *
 * An administrator moves between these two apps all day. `Estate`, `Data` and
 * `Assurance` mean here exactly what they mean there — what is running and
 * what it costs, what goes in and out, and what actually happened — so the
 * second app costs nothing to learn.
 *
 * There is no `Access` group, because this app grants nothing and has no
 * screen that would sit in one. An empty heading to complete a set would be
 * a symmetry that lied.
 *
 * `This app` is under **Assurance** rather than standing apart. It is the
 * screen on which the tool that shows you every agent's ceiling shows you its
 * own, and "does this thing hold itself to what it claims" is the question
 * that group is for.
 */
export const GROUPS = [
  {
    id: "estate",
    label: "Estate",
    blurb: "Which agents exist, and what they cost to run.",
  },
  {
    id: "data",
    label: "Data",
    blurb: "Where each agent reads from, and what leaves here.",
  },
  {
    id: "assurance",
    label: "Assurance",
    blurb: "What they did, whether a person was there, and what this app"
      + " itself may touch.",
  },
];

export const SECTIONS = [
  {
    id: "agents",
    group: "estate",
    label: "Agents",
    question: "Who is acting inside this kernel?",
    component: Agents,
    // Plain prose, no backticks: `Permission` renders `gate` as text rather
    // than as markdown, so a backtick here is a backtick on screen.
    gate:
      "Governed by one declared action — audit.read — and not by" +
      " tags. This" +
      " app declares that action and no business type at all, so nothing on" +
      " this screen is a record: it is the list of agents every app's" +
      " manifest declares, and the provenance envelope of writes attributed" +
      " to one. The log is returned whole, including rows naming records you" +
      " cannot open, because a log narrowed to what you may see can hide the" +
      " thing you are looking for — and it names a record rather than" +
      " opening one.",
  },
  {
    id: "activity",
    group: "assurance",
    label: "Activity",
    question: "What have the agents actually done?",
    component: Activity,
    gate:
      "Governed by one declared action — audit.read — and not by tags. The" +
      " rows are read per agent rather than out of one page of the log, so" +
      " this is each agent's own history and not a sample of everything the" +
      " kernel recorded. Nothing here is a record: it is the provenance" +
      " envelope of writes attributed to a declared agent, plus the cadence" +
      " somebody set for each one.",
  },
  {
    id: "spending",
    group: "estate",
    label: "Spending",
    question: "What are they costing, and for what?",
    component: Spending,
    gate:
      "Governed by one declared action — audit.read — and not by tags. Two" +
      " reads meet on this screen and neither is a record: what each agent's" +
      " runs reported they cost, which the agent files about itself, and a" +
      " count of the log rows naming it, which the kernel wrote. The second" +
      " is a walk that can stop early, so a change count is a floor wherever" +
      " it did — which is why the cost is exact on this screen and the rate" +
      " beside it is not.",
  },
  {
    id: "wiring",
    group: "data",
    label: "Wiring",
    question: "Where does each agent's data come from?",
    component: Wiring,
    gate:
      "Governed by having a manifest, not by tags. Nothing on this screen is" +
      " a record: it is every app's declared service reads, every connector's" +
      " count of what it has written, and the agents each manifest declares —" +
      " joined on a type. Two of the three hops it draws are decisions" +
      " somebody reviewed and the third is a count of what has happened, and" +
      " the picture draws them differently for that reason.",
  },
  {
    id: "knowledge",
    group: "data",
    label: "Knowledge",
    question: "What have they learned, and what have we told them?",
    component: Knowledge,
    // Between `Wiring` and `Outbound` on purpose: what comes in, what is
    // kept, what leaves. The brain is a new answer to the question `Wiring`
    // asks — *where does each agent's data come from* — and the first one
    // where the answer is "from a previous run of itself".
    gate:
      "Governed by a declared read of Memory — customer, vendor and" +
      " employee — and by three declared actions: memory.create," +
      " memory.update and memory.delete. It is the first screen in this app" +
      " that shows a record rather than a declaration or a count, so the" +
      " intersection applies here where it does not elsewhere: a memory you" +
      " may not see is not on this list. That stopped being trivial: a" +
      " memory now inherits the tags of the records it was drawn from, so a" +
      " conclusion an agent reached about a customer is itself tagged" +
      " customer — computed by the kernel from what it cites, never" +
      " chosen by whoever wrote it. This line is now what decides who sees" +
      " an inference about a customer, and a memory drawn from something" +
      " you cannot read is absent from this list and from the count above" +
      " it. What this app deliberately cannot read is the evidence itself:" +
      " Invoice, Organization and Person are not declared here, so a" +
      " conclusion resting on them is reported as out of reach rather than" +
      " re-checked, and out of reach is not the same as stale. Writing" +
      " grants nothing: tag options come from your own permitted tags and" +
      " never a list this screen holds, so nobody can write an entry more" +
      " visible than themselves. Deleting is the one thing here a schedule" +
      " cannot do — it needs a person, because it has no undo.",
  },
  {
    id: "outbound",
    group: "data",
    label: "Outbound",
    question: "What can leave here, and what has?",
    component: Outbound,
    gate:
      "Governed by having a manifest and by one declared action —" +
      " audit.read — and not by tags. Nothing on this screen is a record and" +
      " no recipient appears on it: the log names each send by an id rather" +
      " than by who it went to, so this page says that something left and to" +
      " nobody in particular. It is the only category of write the kernel" +
      " cannot check against anything it still holds, which is why it has a" +
      " screen of its own rather than a column on another.",
  },
  {
    id: "attended",
    group: "assurance",
    label: "Attended",
    question: "How much of this happened without a person?",
    component: Attended,
    gate:
      "Governed by the same declared action as the chart — audit.read — and" +
      " not by tags. Nothing on this screen is a record and no row is shown:" +
      " it is a count of the provenance envelope's mode field, which the" +
      " kernel stamps from the shape of the token and no app can assert." +
      " Rows written before that field existed carry no mode and are counted" +
      " on their own rather than assumed to be either.",
  },
  {
    id: "this-app",
    group: "assurance",
    label: "This app",
    question: "What may Agent OS itself touch?",
    component: ThisApp,
    gate:
      "Governed by having a manifest, not by tags. Every app the kernel" +
      " knows may read every app's ceiling, this one included — one kernel" +
      " per org (LO-3), so everything it could list belongs to you. This" +
      " screen shows only this app's own row, because a second register of" +
      " every app is the console's job and two of them would eventually" +
      " disagree.",
  },
];
